import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTranslatedPageData } from "../utils/projectTranslations";
import ProjectCard from "./ui/ProjectCard";
import Button from "./ui/Button";
import { cn, spacing, animations } from "../utils/cn";
import { useNavigation } from "../contexts/NavigationContext";

const OtherProjects = () => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);
  const location = useLocation();
  const { rememberLocation } = useNavigation();
  const translatedPageData = useTranslatedPageData();

  // Get remaining projects (excluding first 3 featured ones)
  const otherProjects = translatedPageData.slice(3);
  const initialProjectsCount = 6;
  const displayedProjects = showMore ? otherProjects : otherProjects.slice(0, initialProjectsCount);

  const handleViewDetailsClick = () => {
    // Remember current location (including hash) and scroll position
    const fullLocation = location.pathname + location.hash;
    rememberLocation(fullLocation, window.scrollY);
  };

  const handleViewArchiveClick = () => {
    // Remember current location (including hash) and scroll position
    const fullLocation = location.pathname + location.hash;
    rememberLocation(fullLocation, window.scrollY);
  };

  return (
    <section className="bg-navy py-16 sm:py-20 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-semibold text-lighterSlate mb-2">
              {t('work.otherProjects')}
            </h2>
            <p className="text-accent font-mono text-sm">
              <Link
                to="/archive"
                className="hover:underline"
                onClick={handleViewArchiveClick}
              >
                {t('work.viewArchive')}
              </Link>
            </p>
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {displayedProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ 
                  y: -8,
                  scale: 1.02,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                className="bg-lightNavy p-5 sm:p-6 lg:p-7 rounded hover:bg-lightNavy/80 transition-all duration-300 group cursor-pointer"
                data-project="other"
              >
                {/* Header with folder and links */}
                <div className="flex items-center justify-between mb-7">
                  <div className="text-accent">
                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                    </svg>
                  </div>
                  
                  <div className="flex gap-4">
                    {project.github && (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lightSlate hover:text-accent transition-colors duration-200"
                        aria-label="GitHub"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                      </a>
                    )}
                    
                    <a
                      href={project.live}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lightSlate hover:text-green transition-colors duration-200"
                      aria-label="External Link"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>

                {/* Project Title */}
                <h3 className="text-xl font-semibold text-lighterSlate mb-3 group-hover:text-accent transition-colors duration-200">
                  <a 
                    href={project.live} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {project.title}
                  </a>
                </h3>

                {/* Project Description */}
                <p className="text-slate text-sm leading-relaxed mb-4">
                  {project.description}
                </p>

                {/* View Details Link */}
                <div className="mb-4">
                  <Link
                    to={`/project-detail/${project.id}`}
                    onClick={handleViewDetailsClick}
                    className="inline-flex items-center text-accent hover:text-lighterSlate transition-colors duration-200 text-sm font-medium"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t('work.viewDetails')}
                  </Link>
                </div>

                {/* Tech Stack */}
                <ul className="flex flex-wrap gap-3 text-xs font-mono text-slate">
                  {project.techStack.slice(0, 4).map((tech, techIndex) => (
                    <li key={techIndex}>{tech}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="text-center mt-12">
            {otherProjects.length > initialProjectsCount && (
              <div>
                <Button
                  onClick={() => setShowMore(!showMore)}
                  size="md"
                  magnetic={true}
                >
                  {showMore ? t('work.showLess') : t('work.showMoreProjects')}
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default OtherProjects;